import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import archive1 from "@/assets/archive-1.jpg";
import bannerVideo from "@/assets/dl-banner-2.mp4.asset.json";
import aboutBinocularsAsset from "@/assets/about-binoculars-blue.png.asset.json";

import { cmsQueryOptions, field, richText } from "@/lib/cms";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Double Lion" },
      { name: "description", content: "Double Lion is a limited edition studio obsessed with detail, material, and permanence. Learn about our philosophy and craft." },
      { property: "og:title", content: "About — Double Lion" },
      { property: "og:description", content: "Double Lion is a limited edition studio obsessed with detail, material, and permanence." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://double-lion-muse.lovable.app/about" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://double-lion-muse.lovable.app/about" },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const { data: cms } = useQuery(cmsQueryOptions);
  const about = cms?.about;
  const body = richText(
    about?.body,
    [
      "Double Lion is not a brand in the conventional sense. It is a studio of limited releases — small batches of objects conceived as tools first, and statements second. Every piece is designed to do one thing exceptionally well, then get out of the way.",
      "Founded by two brothers that grew up in and around the ocean, Double Lion was created to manufacture products that encourage adventure and observation whether its in your backyard or on a new continent on an expedition. \n\n\nOur binoculars are meant to be taken everywhere. They are equally fit to live in your car's cupholder to check the waves every morning or to be with you on the west face of El Capitan while big wall climbing. Double Lion binoculars are made to last by manufacture craftsman that have been building high quality binoculars for over give generations. ",
      "",
    ].join("\n\n"),
  );
  const paragraphs = body.split(/\n\n+/).filter((p) => p.trim().length > 0);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-24 pb-32">
        {/* Hero text */}
        <section className="px-6 pt-16 md:pt-24">
          <div className="mx-auto max-w-4xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-8">
              {field(about, "section_number", "01 — About")}
            </div>
            <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight">
              {richText(about?.headline, "We're building this brand and our products around things that motivate us to observe our natural world and designing hardware that can be used everywhere you go for a very long time.")}
            </h1>
          </div>
        </section>

        {/* Hero Image */}
        <section className="px-6 mt-20 md:mt-28">
          <div className="mx-auto max-w-6xl">
            <video
              src={bannerVideo.url}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label="Double Lion binoculars in the field"
              className="w-full h-[420px] md:h-[640px] object-cover"
            />
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              IN THE FIELD — BERGEN, NORWAY
            </p>
          </div>
        </section>

        {/* Editorial body */}
        <section className="px-6 mt-20 md:mt-32">
          <div className="mx-auto max-w-3xl space-y-8 font-sans text-base md:text-lg text-foreground/80 leading-relaxed">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>

        {/* Pull quote */}
        <section className="px-6 mt-24 md:mt-32">
          <div className="mx-auto max-w-4xl border-t border-b border-border/60 py-16">
            <p className="font-serif text-2xl md:text-4xl leading-snug tracking-tight">
              "We wanted an object that disappears into the moment — light enough to forget,
              precise enough to remember."
            </p>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              — The Founders
            </p>
          </div>
        </section>

        {/* Two-up: image + values */}
        <section className="px-6 mt-24 md:mt-32">
          <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <img
              src={aboutBinocularsAsset.url}
              alt="Blue and silver Double Lion binoculars on a deep blue background"
              loading="lazy"
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <div className="space-y-10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                  02 — Material
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">Machined aluminum.</h3>
                <p className="text-foreground/75 leading-relaxed">
                  Each housing is CNC-machined from a single aluminum billet and
                  hard-anodized for a finish that resists salt, sun, and the everyday
                  abuse of being carried from the cockpit to the pilothouse.
                </p>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                  03 — Optics
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">Glass that earns its place.</h3>
                <p className="text-foreground/75 leading-relaxed">
                  Multi-coated, high-index lenses tuned for edge-to-edge clarity. No
                  fringing, no compromise — the kind of glass you stop noticing the
                  moment you start looking through it.
                </p>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                  04 — Weight
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">Built to be carried.</h3>
                <p className="text-foreground/75 leading-relaxed">
                  Small enough for a jacket pocket, light enough to forget you brought
                  them — until the moment you need them.
                </p>
              </div>
            </div>
          </div>
          <p lang="ja" className="mx-auto max-w-6xl mt-12 text-center font-serif text-lg md:text-2xl tracking-[0.15em] text-foreground/80">
            メイド・イン・ジャパン
          </p>
        </section>


        {/* Numbers strip */}
        <section className="px-6 mt-24 md:mt-32">
          <div className="mx-auto max-w-6xl grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-border/60 pt-12">
            {[
              { k: "Magnification", v: "6×" },
              { k: "Weight", v: "145g" },
              { k: "Edition", v: "300 Pieces" },
              { k: "Warranty", v: "5 Years" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-serif text-3xl md:text-5xl tracking-tight">{s.v}</div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 mt-24 md:mt-32">
          <div className="mx-auto max-w-3xl flex flex-col sm:flex-row gap-6">
            <Link
              to="/story"
              className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
            >
              Read the backstory →
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
            >
              Explore the product →
            </Link>
          </div>
        </section>

      </main>

    </div>
  );
}
